import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, Users, Building2, TrendingUp, Eye, Share2, 
  Award, Link2, Briefcase, FileText, CheckCircle, XCircle,
  ArrowLeft, RefreshCw, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const MetricCard = ({ icon: Icon, title, value, subtitle, color = "indigo" }) => {
  const colorClasses = {
    indigo: "bg-indigo-100 text-indigo-600",
    green: "bg-green-100 text-green-600",
    purple: "bg-purple-100 text-purple-600",
    blue: "bg-blue-100 text-blue-600",
    amber: "bg-amber-100 text-amber-600",
    rose: "bg-rose-100 text-rose-600",
    cyan: "bg-cyan-100 text-cyan-600",
    emerald: "bg-emerald-100 text-emerald-600"
  };

  return (
    <Card className="p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </Card>
  );
};

export const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/admin`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const creatorTrend = analytics?.trends?.creator_signups || [];
  const brandTrend = analytics?.trends?.brand_signups || [];
  const applicationTrend = analytics?.trends?.applications || [];
  const referralTrend = analytics?.trends?.referrals || [];

  // Combined signup trend for chart
  const signupTrendData = creatorTrend.map((item, idx) => ({
    date: item.date.slice(5), // Show MM-DD
    creators: item.count,
    brands: brandTrend[idx]?.count || 0
  }));

  // Application and referral combined
  const activityTrendData = applicationTrend.map((item, idx) => ({
    date: item.date.slice(5),
    applications: item.count,
    referrals: referralTrend[idx]?.count || 0
  }));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={loadAnalytics} data-testid="refresh-analytics-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-8 h-8 text-indigo-600" />
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily: 'Manrope'}} data-testid="analytics-heading">
              Analytics Dashboard
            </h1>
          </div>
          <p className="text-slate-600">Comprehensive platform metrics and insights</p>
        </div>

        {/* Creator Metrics */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Creator Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <MetricCard
              icon={Users}
              title="Total Creators"
              value={analytics?.creators?.total || 0}
              color="indigo"
            />
            <MetricCard
              icon={Eye}
              title="Profile Views"
              value={analytics?.creators?.profile_views || 0}
              color="blue"
            />
            <MetricCard
              icon={Share2}
              title="Report Shares"
              value={analytics?.creators?.report_shares || 0}
              color="purple"
            />
            <MetricCard
              icon={Award}
              title="Badge Impressions"
              value={analytics?.creators?.badge_impressions || 0}
              color="amber"
            />
            <MetricCard
              icon={Link2}
              title="Referral Clicks"
              value={analytics?.creators?.referral_clicks || 0}
              color="cyan"
            />
            <MetricCard
              icon={CheckCircle}
              title="Referral Conversions"
              value={analytics?.referrals?.completed || 0}
              subtitle={`${analytics?.referrals?.conversion_rate || 0}% rate`}
              color="green"
            />
          </div>
        </div>

        {/* Brand Metrics */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Brand Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={Building2}
              title="Brands Registered"
              value={analytics?.brands?.total_registered || 0}
              color="purple"
            />
            <MetricCard
              icon={Eye}
              title="Profiles Viewed"
              value={analytics?.brands?.profiles_viewed || 0}
              color="blue"
            />
            <MetricCard
              icon={Award}
              title="Bookmarks Saved"
              value={analytics?.brands?.bookmarks_saved || 0}
              color="amber"
            />
            <MetricCard
              icon={Briefcase}
              title="Campaigns Created"
              value={analytics?.campaigns?.total || 0}
              subtitle={`${analytics?.campaigns?.active || 0} active`}
              color="indigo"
            />
          </div>
        </div>

        {/* Marketplace Metrics */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Marketplace Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={Briefcase}
              title="Total Campaigns"
              value={analytics?.marketplace?.total_campaigns || 0}
              color="indigo"
            />
            <MetricCard
              icon={FileText}
              title="Total Applications"
              value={analytics?.applications?.total || 0}
              color="blue"
            />
            <MetricCard
              icon={TrendingUp}
              title="Match Counts"
              value={analytics?.marketplace?.match_counts || 0}
              color="purple"
            />
            <MetricCard
              icon={Users}
              title="Referral Signups"
              value={analytics?.marketplace?.referral_signups || 0}
              color="green"
            />
          </div>
        </div>

        {/* Application Stats Card */}
        <div className="mb-8">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>Application Status Breakdown</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-amber-100 flex items-center justify-center">
                  <FileText className="w-8 h-8 text-amber-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                  {analytics?.applications?.pending || 0}
                </p>
                <p className="text-sm text-slate-500">Pending</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                  {analytics?.applications?.accepted || 0}
                </p>
                <p className="text-sm text-slate-500">Accepted</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-rose-100 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-rose-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                  {analytics?.applications?.rejected || 0}
                </p>
                <p className="text-sm text-slate-500">Rejected</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-indigo-100 flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-indigo-600" />
                </div>
                <p className="text-2xl font-bold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                  {analytics?.applications?.acceptance_rate || 0}%
                </p>
                <p className="text-sm text-slate-500">Acceptance Rate</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Signup Trends */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
              Signup Trends (30 Days)
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
                <AreaChart data={signupTrendData}>
                  <defs>
                    <linearGradient id="creatorGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="brandGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#7C3AED" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{fontSize: 11}} stroke="#94A3B8" />
                  <YAxis tick={{fontSize: 11}} stroke="#94A3B8" />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Legend wrapperStyle={{fontSize: '12px'}} />
                  <Area type="monotone" dataKey="creators" name="Creators" stroke="#4F46E5" fill="url(#creatorGradient)" strokeWidth={2} />
                  <Area type="monotone" dataKey="brands" name="Brands" stroke="#7C3AED" fill="url(#brandGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Activity Trends */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
              Platform Activity (30 Days)
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
                <LineChart data={activityTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{fontSize: 11}} stroke="#94A3B8" />
                  <YAxis tick={{fontSize: 11}} stroke="#94A3B8" />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Legend wrapperStyle={{fontSize: '12px'}} />
                  <Line type="monotone" dataKey="applications" name="Applications" stroke="#10B981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="referrals" name="Referrals" stroke="#F59E0B" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Referral Growth Chart */}
        <Card className="p-6 mb-8">
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
            Referral Growth Trend
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
              <BarChart data={referralTrend.slice(-14).map(item => ({
                date: item.date.slice(5),
                count: item.count
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{fontSize: 11}} stroke="#94A3B8" />
                <YAxis tick={{fontSize: 11}} stroke="#94A3B8" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Bar dataKey="count" name="Referrals" fill="#4F46E5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Quick Stats Footer */}
        <Card className="p-6 bg-gradient-to-br from-indigo-50 to-slate-50">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{fontFamily: 'Manrope'}}>Platform Summary</h3>
              <p className="text-sm text-slate-600">
                {analytics?.creators?.total || 0} creators | {analytics?.brands?.total_registered || 0} brands | {analytics?.campaigns?.total || 0} campaigns
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                  {analytics?.users?.total || 0}
                </p>
                <p className="text-xs text-slate-500">Total Users</p>
              </div>
              <div className="h-10 w-px bg-slate-200"></div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600" style={{fontFamily: 'JetBrains Mono'}}>
                  {analytics?.applications?.acceptance_rate || 0}%
                </p>
                <p className="text-xs text-slate-500">Success Rate</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalytics;
